import {
  signIn as authSignIn,
  signOut as authSignOut,
  completeNewPassword,
  getCognitoUserUsername,
  getCurrentAuthUser,
  isCognitoUserEmailVerified,
  sendForgotPasswordEmail,
  submitForgotPassword,
} from '@akin-home/auth/frontend';

import { CognitoUser } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';

import { ReactNode, useEffect } from 'react';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';

type SignInResponse =
  | { auth_user: CognitoUser; error: undefined; challengeName: undefined }
  | { auth_user: undefined; error: SignInError; challengeName: undefined }
  | {
      auth_user: undefined;
      error: undefined;
      challengeName: 'NEW_PASSWORD_REQUIRED';
    };

/* eslint-disable-next-line */
export interface AuthProviderProps {
  /** View to load when application has not been loaded */
  unauthenticated_view: ReactNode;

  /** View to load when authentication is loading */
  loading_view?: ReactNode;

  children?: ReactNode;
  /** Callback that is triggered when AuthProvider has completed its processing */
  onLoadComplete?(): void;

  /** Callback to be called when signed out */
  onSignOut?(): void;

  /** Optional callback to validate the user. Return { error } if validation fails*/
  validateUser?(auth_user: CognitoUser): undefined | { error: string };
}

interface SignInError {
  code: string;
  message: string;
}

interface AuthStore {
  authenticated: boolean;

  /** Attempting to authenticate the user */
  authenticating?: boolean;

  /** The auth sta */
  loading: boolean;
  /** The current authenticated user */
  auth_user?: CognitoUser;

  authenticate(user?: CognitoUser): void;
  partial_authenticate(user: CognitoUser): void;
  deauthenticate(): void;

  setAuthenticating(authenticating: boolean): void;

  username?: string;
  setUsername(username: string): void;

  stage: LoginStage;
  goToForgotPassword(): void;
  goBackToLogin(): void;

  signIn(username: string, password: string): Promise<SignInResponse>;

  signOut(): void;
  signOutCallback?: () => void;
  setSignoutCallback(callback: () => void): void;

  submitNewPassword(
    password: string
  ): Promise<{ error?: string; success: boolean }>;

  // Forgot password actions
  resetting_password?: boolean;
  sendResetCode(
    username?: string
  ): Promise<{ success: boolean; error?: string }>;
  submitForgotPassword(
    reset_code: string,
    password: string
  ): Promise<{ success: boolean; error?: string }>;
}

export type LoginStage =
  | 'login' // The main login screen
  | 'forgot_password_enter_username'
  | 'forgot_password_enter_code'
  | 'enter_new_password'; // This is for when a password has been reset, and now needs to be updated by the user

export const useAuthStore = create<AuthStore>((set, get) => ({
  authenticated: false,
  loading: true,
  authenticate(auth_user) {
    set({ auth_user, authenticated: true, loading: false });
  },
  partial_authenticate(auth_user) {
    set({ auth_user, authenticated: false, loading: false });
  },
  deauthenticate() {
    set({ auth_user: undefined, loading: false, authenticated: false });
  },

  setAuthenticating(authenticating) {
    set({ authenticating });
  },

  setUsername(username) {
    set({ username });
  },

  stage: 'login',
  goToForgotPassword() {
    set({ stage: 'forgot_password_enter_username' });
  },
  goBackToLogin() {
    set({ stage: 'login' });
  },

  signOut() {
    authSignOut();
    set({ auth_user: undefined, loading: false, authenticated: false });
    get().signOutCallback?.();
  },

  setSignoutCallback(callback) {
    set({ signOutCallback: callback });
  },

  async signIn(username, password) {
    const { setAuthenticating } = get();

    try {
      get().setAuthenticating(true);

      const auth_user = await authSignIn(username, password);

      setAuthenticating(false);

      if (auth_user.challengeName) {
        get().partial_authenticate(auth_user);

        return {
          challengeName: auth_user.challengeName,
        };
      }

      const verified = isCognitoUserEmailVerified(auth_user);

      if (!verified) {
        get().signOut();

        return {
          error: { code: 'validation_error', message: 'Email not verified' },
        };
      }

      get().authenticate(auth_user);

      return { auth_user };
    } catch (ex: any) {
      setAuthenticating(false);

      return {
        error: {
          code: (ex.code || 'unknown') as string,
          message: (ex.message || 'Oops, something went wrong') as string,
        },
      };
    }
  },

  async submitNewPassword(password) {
    const { auth_user, authenticate } = get();

    if (auth_user) {
      const res = await completeNewPassword(auth_user, password);

      if (!res.error) {
        authenticate(await getCurrentAuthUser()); // Refresh the current auth user

        return { success: true };
      }

      return { error: res.error, success: false };
    }

    return { error: 'No current user', success: false };
  },

  async sendResetCode(username) {
    if (!username?.length) {
      // showErrorToast({ text1: 'Please enter a username' });
      return { success: false, error: 'Please enter a username' };
    }

    const result = await sendForgotPasswordEmail(username);

    if ('error' in result) {
      // showErrorToast({ text1: 'Incorrect Username' });
      console.log('Error sending reset code: ', result.error);
      return { success: false, error: 'Incorrect Username' };
    }

    set({ username, stage: 'forgot_password_enter_code' });
    return { success: true };
  },

  async submitForgotPassword(reset_code, new_password) {
    const username = get().username;

    if (!reset_code?.length) {
      // showErrorToast({ text1: 'Please enter a reset code' });
      return { success: false, error: 'Please enter a reset code' };
    }

    if (!new_password?.length) {
      // showErrorToast({ text1: 'Please enter a password' });
      return { success: false, error: 'Please enter a password' };
    }

    if (!username?.length) {
      set({ stage: 'login' });
      return { success: false, error: 'Incorrect username' };
    }

    set({ resetting_password: true });

    const { error } = await submitForgotPassword(
      username,
      reset_code,
      new_password
    );

    if (error) {
      set({ resetting_password: false });
      // showErrorToast({ text1: error });
      return { success: false, error: error };
    }

    const signInRes = await get().signIn(username, new_password);
    if ('error' in signInRes) {
      set({ resetting_password: false });
      // showErrorToast({ text1: signInRes.error?.message });
      return { success: false, error: signInRes.error?.message };
    } else {
      set({ resetting_password: false, stage: 'login' });
      return { success: true };
    }
  },
}));

export function AuthProvider(props: AuthProviderProps): JSX.Element {
  const {
    loading,
    authenticated,
    authenticate,
    deauthenticate,
    setSignoutCallback,
  } = useAuthStore();

  const show_loading = loading && !!props.loading_view;

  useEffect(() => {
    props.onSignOut && setSignoutCallback(props.onSignOut);
  }, [props.onSignOut]);

  useEffect(() => {
    (async () => {
      const auth_user = await getCurrentAuthUser();

      if (auth_user) {
        const verified = isCognitoUserEmailVerified(auth_user);

        if (!verified) {
          deauthenticate();
        } else {
          authenticate(auth_user);
        }
      } else {
        deauthenticate();
      }
    })();

    /** Listen to auth events to update state */
    Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signOut') {
        props.onSignOut?.();
        deauthenticate();
      }
    });
  }, []);

  return (
    authenticated
      ? props.children
      : show_loading
      ? props.loading_view
      : props.unauthenticated_view
  ) as JSX.Element;
}

export const useAuthState = () =>
  useAuthStore(
    ({ authenticated, auth_user, loading }) => ({
      authenticated,
      auth_user,
      loading,
    }),
    shallow
  );

export const useAuthUser = () => useAuthStore((state) => state.auth_user);

export const useAuthUsername = () =>
  useAuthStore((state) => getCognitoUserUsername(state.auth_user));

export const useLoginStage = () => useAuthStore((state) => state.stage);

export const useSignIn = () => useAuthStore((state) => state.signIn);
export const useSignOut = () => useAuthStore((state) => state.signOut);
export const useSubmitNewPassword = () =>
  useAuthStore((state) => state.submitNewPassword);
export const useSendResetCode = () =>
  useAuthStore((state) => state.sendResetCode);

export default AuthProvider;
